import React, { FC, useRef, useCallback, useEffect } from 'react';

import { Box } from '@vimeo/bokeh';

import ReviewAsGuestForm from './ReviewAsGuestForm';

interface Props {
  xsrft: string;
  isActive: boolean;
  onDismiss: () => void;
  onSuccessfulLogin: (response: { signup: boolean }) => void;
  submitCommentAsGuest: (guestName: string, guestEmail?: string) => void;
  isSubmitLoading?: boolean;
  apiErrorCode?: number | null;
  resetApiErrorCode?: (errorCode: number) => void | undefined;
  titleText?: string;
  submitButtonText?: string;
  namePlaceholderText?: string;
  isCompact?: boolean;
  canReactToCollabComments?: boolean;
}

const InlineReviewAsGuestPrompt: FC<Props> = ({
  xsrft,
  isActive,
  onDismiss,
  onSuccessfulLogin,
  submitCommentAsGuest,
  isSubmitLoading = false,
  apiErrorCode = null,
  resetApiErrorCode,
  titleText,
  submitButtonText,
  namePlaceholderText,
  isCompact = false,
  canReactToCollabComments,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isLoginModalOpenRef = useRef(false);

  const handleLoginModalOpenChange = useCallback((isOpen: boolean) => {
    isLoginModalOpenRef.current = isOpen;
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const onPointerDown = (e: PointerEvent) => {
      if (isLoginModalOpenRef.current) return;
      if (wrapperRef.current?.contains(e.target as Node)) return;
      onDismiss();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [isActive, onDismiss]);

  return (
    <Box
      ref={wrapperRef}
      width="100%"
      display={isActive ? undefined : 'none'}
      bg="fill-surface"
      borderRadius="input-md"
      border="1px solid"
      borderColor="input-stroke"
    >
      <ReviewAsGuestForm
        closeModal={onDismiss}
        xsrft={xsrft}
        onSuccessfulLogin={onSuccessfulLogin}
        submitCommentAsGuest={submitCommentAsGuest}
        isSubmitLoading={isSubmitLoading}
        apiErrorCode={apiErrorCode}
        resetApiErrorCode={resetApiErrorCode}
        titleText={titleText}
        submitButtonText={submitButtonText}
        namePlaceholderText={namePlaceholderText}
        isCompact={isCompact}
        canReactToCollabComments={canReactToCollabComments}
        onLoginModalOpenChange={handleLoginModalOpenChange}
      />
    </Box>
  );
};

export default InlineReviewAsGuestPrompt;
